"use client";

import React from "react";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { StarIcon as StarOutline } from "@heroicons/react/24/outline";
import { Label } from "@/components/ui/label";
import { Brand, Category } from "@/lib/types/product";

export default function ProductFilters({
  categories,
  brands,
  checkedCategories,
  checkedBrands,
  onCategoryChange,
  onBrandChange,
  priceRange = [5000, 700000],
  onPriceRangeChange,
  minPrice = 0,
  maxPrice = 1000000,
  hiddenCategoryIds = [],
  hiddenBrandIds = [],
}: {
  categories: Category[];
  brands: Brand[];
  checkedCategories: { [key: string]: boolean };
  checkedBrands: { [key: string]: boolean };
  onCategoryChange: (categoryId: number, checked: boolean) => void;
  onBrandChange: (brandId: number, checked: boolean) => void;
  priceRange?: [number, number];
  onPriceRangeChange?: (range: [number, number]) => void;
  minPrice?: number;
  maxPrice?: number;
  hiddenCategoryIds?: number[];
  hiddenBrandIds?: number[];
}) {
  // Filter out hidden categories and brands
  const visibleCategories = categories.filter((cat) => !hiddenCategoryIds.includes(cat.id));
  const visibleBrands = brands.filter((brand) => !hiddenBrandIds.includes(brand.id));
  return (
    <div className='divide-border-primmary/50 divide-y-2'>
      <div className='flex flex-col gap-2 pb-6'>
        <p className='text-text-title text-xl font-medium'>Filter by Price</p>
        <Slider
          defaultValue={priceRange}
          min={minPrice}
          max={maxPrice}
          tooltip
          formatValue={(v) => `₦${v.toLocaleString()}`}
          onValueChange={onPriceRangeChange}
        />
      </div>

      <div className='flex flex-col gap-2 py-6'>
        <p className='text-text-title text-xl font-medium'>Categories</p>
        <Command className='scrollbar-hide bg-transparent'>
          <CommandInput placeholder='Search for a category' />
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandList className='scrollbar-hide max-h-[200px] min-h-[200px]'>
            {visibleCategories.map((category) => (
              <CommandItem key={category.id} asChild>
                <Label
                  htmlFor={`category-${category.id}`}
                  className={`bg-bg-muted dark:bg-bg-base dark:border-border-primmary dark:text-text-title my-4 w-fit cursor-pointer rounded-full px-5 text-base font-semibold dark:border ${
                    checkedCategories[category.id]
                      ? "bg-btn-primary data-[selected=true]:bg-btn-primary data-[selected=true]:text-text-title-inv text-text-title-inv dark:bg-btn-primary dark:text-text-title-inv"
                      : "data-[selected=true]:bg-bg-muted dark:data-[selected=true]:bg-bg-base"
                  }`}
                >
                  <Checkbox
                    id={`category-${category.id}`}
                    checked={checkedCategories[category.id]}
                    onCheckedChange={(checked) => {
                      onCategoryChange(category.id, checked === true);
                    }}
                    className='hidden'
                  />
                  {category.name}
                  {category.count !== undefined && ` (${category.count})`}
                </Label>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </div>

      <div className='flex flex-col gap-3 py-6'>
        <p className='text-text-title text-xl font-medium'>Product Rating</p>
        <div className='flex flex-col gap-2'>
          {[5, 4, 3, 2, 1].map((rating) => (
            <label key={rating} className='flex cursor-pointer items-center gap-3'>
              <Checkbox
                className='data-[state=checked]:bg-link-primary data-[state=checked]:border-link-primary size-3'
                aria-label={`${rating} star${rating > 1 ? "s" : ""} & up`}
              />
              <div className='flex items-center gap-1'>
                {Array.from({ length: rating }).map((_, i) => (
                  <StarSolid key={`solid-${rating}-${i}`} className='text-link-primary size-3.5' />
                ))}
                {Array.from({ length: 5 - rating }).map((_, i) => (
                  <StarOutline key={`outline-${rating}-${i}`} className='text-icon-muted size-3.5' />
                ))}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className='flex flex-col gap-2 py-6'>
        <p className='text-text-title text-xl font-medium'>Brands</p>
        <Command className='scrollbar-hide bg-transparent'>
          <CommandInput placeholder='Search for a brand' />
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandList className='scrollbar-hide max-h-[200px] min-h-[200px]'>
            {visibleBrands.map((brand) => (
              <CommandItem key={brand.id} asChild>
                <Label
                  htmlFor={`brand-${brand.id}`}
                  className={`bg-bg-muted dark:bg-bg-base dark:border-border-primmary dark:text-text-title my-4 w-fit cursor-pointer rounded-full px-5 text-base font-semibold dark:border ${
                    checkedBrands[brand.id]
                      ? "bg-btn-primary data-[selected=true]:bg-btn-primary data-[selected=true]:text-text-title-inv text-text-title-inv dark:bg-btn-primary dark:text-text-title-inv"
                      : "data-[selected=true]:bg-bg-muted dark:data-[selected=true]:bg-bg-base"
                  }`}
                >
                  <Checkbox
                    id={`brand-${brand.id}`}
                    checked={checkedBrands[brand.id]}
                    onCheckedChange={(checked) => {
                      onBrandChange(brand.id, checked === true);
                    }}
                    className='hidden'
                  />
                  {brand.name}
                  {brand.count !== undefined && ` (${brand.count})`}
                </Label>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </div>
    </div>
  );
}
